/**
 * ============================================================================
 * StatsSectionStatic
 * ============================================================================
 *
 * Purpose:
 *   Render corporate/homepage statistics in desktop and mobile layouts.
 *
 * Responsibilities:
 *   - Support static homepage defaults.
 *   - Support CMS-driven statistics.
 *   - Handle desktop hover imagery.
 *   - Render mobile statistics through the shared carousel.
 *
 * ============================================================================
 */

"use client";

import { useState } from "react";

import UnivoCarousel from "@/components/common/UnivoCarousel";
import UnivoImage from "@/components/common/UnivoImage";
import { IMAGE_ASSETS } from "@/constants/images";

import styles from "./StatsSectionStatic.module.scss";

// ============================================================================
// Types
// ============================================================================

export interface StatItem {
  number: string;
  label: string;
  description?: string;
  image?: string;
  hoverimage?: string;
}

interface StatsSectionStaticProps {
  stats?: StatItem[];
}

// ============================================================================
// Default data
// ============================================================================

const DEFAULT_STATS: StatItem[] = [
  {
    number: "250,000+",
    label: "Learners",
    description: "Moving Forward With",
    image: IMAGE_ASSETS.statsLearners,
    hoverimage: IMAGE_ASSETS.statsLearnersHover,
  },
  {
    number: "150+",
    label: "Countries",
    description: "GLOBAL PRESENCE ACROSS",
    image: IMAGE_ASSETS.statsCountries,
    hoverimage: IMAGE_ASSETS.statsCountriesHover,
  },
  {
    number: "16,500+",
    label: "Pincodes",
    description: "NATIONWIDE PRESENCE ACROSS",
    image: IMAGE_ASSETS.statsPincodes,
    hoverimage: IMAGE_ASSETS.statsPincodesHover,
  },
  {
    number: "30+",
    label: "Years",
    description: "ACADEMIC EXCELLENCE OF",
    image: IMAGE_ASSETS.statsYears,
    hoverimage: IMAGE_ASSETS.statsYearsHover,
  },
];

// ============================================================================
// Shared stat content
// ============================================================================

type StatContentProps = {
  stat: StatItem;
};

const StatContent = ({ stat }: StatContentProps) => (
  <div className="flex flex-col gap-1">
    {stat.description && (
      <p className={styles.description}>{stat.description}</p>
    )}

    <p className={styles.number}>{stat.number}</p>

    <UnivoImage
      className="h-0.5 w-11 rounded-full"
      src={IMAGE_ASSETS.statsDivider}
      alt=""
      width={44}
      height={2}
      sizes="44px"
      loading="lazy"
    />

    <p className={styles.label}>{stat.label}</p>
  </div>
);

// ============================================================================
// Desktop stat
// ============================================================================

type DesktopStatProps = {
  stat: StatItem;
  index: number;
  hoveredIndex: number | null;
  onHover: (index: number) => void;
  onLeave: () => void;
};

const DesktopStat = ({
  stat,
  index,
  hoveredIndex,
  onHover,
  onLeave,
}: DesktopStatProps) => {
  const image = hoveredIndex === index
    ? stat.hoverimage ?? stat.image
    : stat.image;
  return (
    <div
      className={styles.statBox}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={onLeave}
    >
      {image && (
        <UnivoImage
          className="lg:h-14 lg:w-14"
          src={image}
          alt=""
          width={56}
          height={56}
          sizes="56px"
          loading="lazy"
        />
      )}

      <StatContent stat={stat} />
    </div>
  );
};

// ============================================================================
// Mobile stat
// ============================================================================

type MobileStatProps = {
  stat: StatItem;
};

const MobileStat = ({ stat }: MobileStatProps) => (
  <div className={styles.mobileSlideContainer}>
    {stat.image && (
      <div
        className={`${styles.imageWrapper} self-start h-7 w-7 lg:h-14 lg:w-14`}
      >
        <UnivoImage
          className={styles.statImageBase}
          src={stat.image}
          alt=""
          width={28}
          height={28}
          sizes="28px"
          loading="lazy"
        />

        {stat.hoverimage && (
          <UnivoImage
            className={styles.statImageHover}
            src={stat.hoverimage}
            alt=""
            width={28}
            height={28}
            sizes="28px"
            loading="lazy"
          />
        )}
      </div>
    )}

    <StatContent stat={stat} />
  </div>
);

// ============================================================================
// Main component
// ============================================================================

const StatsSectionStatic = ({
  stats = DEFAULT_STATS,
}: StatsSectionStaticProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section className={styles.statsContainer}>
      <div className={styles.statsWrapper}>
        {/* Desktop */}
        <div className={styles.desktopStats}>
          {stats.map((stat, index) => (
            <DesktopStat
              key={`${stat.number}-${stat.label}-${index}`}
              stat={stat}
              index={index}
              hoveredIndex={hoveredIndex}
              onHover={setHoveredIndex}
              onLeave={() => setHoveredIndex(null)}
            />
          ))}
        </div>

        {/* Mobile */}
        <div className={styles.mobileStats}>
          <UnivoCarousel preset="statsMobile">
            {stats.map((stat, index) => (
              <MobileStat
                key={`${stat.number}-${stat.label}-${index}`}
                stat={stat}
              />
            ))}
          </UnivoCarousel>
        </div>
      </div>
    </section>
  );
};

export default StatsSectionStatic;